import {
    Autocomplete,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    InputLabel,
    Stack,
    Switch,
    SxProps,
    TextField,
    useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { jobBrokerService, setupService, userService } from "../../../../Core/Api/services";
import { GroupAdvancedInfos } from "../../../../Core/Api/Types/group-advanced-infos";
import { DbJobRuleInfos } from "../../../../Core/Api/Types/db-job-rule-infos";
import FullTask from "../../../../Layouts/FullTask";
import FullTaskTopbar from "../../../../Components/Topbars/FullTaskTopbar";
import FormInputText from "../../../../Components/Forms/FormInputText/form-input-text";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import FormAutocomplete from "../../../../Components/Forms/FormAutocomplete/form-autocomplete";
import { produce } from "immer";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectStatus } from "../../../../Core/Api/Types/object-status";
import { useLocation, useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import { DbStorageInfos } from "../../../../Core/Api/Types/db-storage-infos";
import { DbFormatInfos } from "../../../../Core/Api/Types/db-format-infos";
import { JobRuleTag } from "../../../../Core/Api/Types/job-rule-tag";
import { FileCategory } from "../../../../Core/Api/Types/file-category";
import FilterListIcon from "@mui/icons-material/FilterList";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import InfoIcon from "@mui/icons-material/Info";
import { v4 as uuidv4 } from "uuid";
import { DeleteForm, GetFormIsValid, SetFieldIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../../Redux/redux-store";
import { ModalContext } from "../../../../Components/Modals";
import ModalShowJsonOutput, {
    ModalShowJsonOutputProps,
} from "../../../../Components/Modals/ModalShowJsonOutput/modal-show-json-output";
import { useDispatch } from "react-redux";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import MetadataComparator from "./metadata-comparator";
import EditPostActions from "./edit-post-actions";
import EditJobDefs from "./edit-job-defs";
import { useTranslation } from "react-i18next";

function EditOrCreateJobRule() {
    const { t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const enqueueCustomSnackbar = useCustomSnackbar();
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    const [newJobRule, setNewJobRule] = useState<DbJobRuleInfos | null>(null);
    const [groups, setGroups] = React.useState<GroupAdvancedInfos[]>([]);
    const [storages, setStorages] = React.useState<DbStorageInfos[]>([]);
    const [formats, setFormats] = React.useState<DbFormatInfos[]>([]);
    const [initializeForms, setInitializeForms] = useState(false);

    const formId = "jobRuleForm";
    const isValid =
        useAppSelector(GetFormIsValid(formId)) &&
        newJobRule &&
        newJobRule?.jobsDefinitions &&
        newJobRule?.jobsDefinitions?.length > 0;

    const isEditing = useMemo(() => location.pathname.includes("edit"), [location]);
    const isLoading = useMemo(
        () => groups.length === 0 || storages.length === 0 || formats.length === 0,
        [groups, storages, formats]
    );

    useEffect(() => {
        if (!location) return;

        if (!location.state) navigate(routePaths.settingsJobRules.path);

        setNewJobRule({
            ...location.state,
            jobsDefinitions: (location.state as DbJobRuleInfos).jobsDefinitions?.map((x) => ({ ...x, uuid: uuidv4() })),
            postActions: (location.state as DbJobRuleInfos).postActions?.map((x) => ({ ...x, uuid: uuidv4() })),
        });

        userService.GetGroupsList().then((res) => {
            if (res.groups) setGroups(res.groups);
            else {
                navigate(routePaths.settingsJobRules.path);
                enqueueCustomSnackbar(res.message ?? t("alert:error.fetchingGroups"), "error");
            }
        });

        setupService.GetAllStorages().then((res) => {
            if (res.storages) setStorages(res.storages);
            else {
                navigate(routePaths.settingsJobRules.path);
                enqueueCustomSnackbar(res.message ?? t("alert:error.fetchingStorages"), "error");
            }
        });

        setupService.GetFormats().then((res) => {
            if (res.formats) setFormats(res.formats);
            else {
                navigate(routePaths.settingsJobRules.path);
                enqueueCustomSnackbar(res.message ?? t("alert:error.fetchingFormats"), "error");
            }
        });

        setInitializeForms(true);

        return () => {
            dispatch(DeleteForm({ formId: formId }));
        };
    }, [location.pathname]);

    useEffect(() => {
        if (initializeForms && newJobRule) {
            if (newJobRule.jobsDefinitions && newJobRule.jobsDefinitions.length > 0) {
                newJobRule.jobsDefinitions.forEach((x) => {
                    dispatch(
                        SetFieldIsValid({
                            fieldId: `jobDefName-${x.uuid}`,
                            formId: formId,
                            formSectionId: x.uuid!,
                            fieldIsValid: x.name && x.name !== "" ? true : false,
                            fieldErrorMessage: "",
                        })
                    );
                });
            }
            if (newJobRule.postActions && newJobRule.postActions.length > 0) {
                newJobRule.postActions.forEach((x) => {
                    dispatch(
                        SetFieldIsValid({
                            fieldId: `postActionJobStatusTrigger-${x.uuid}`,
                            formId: formId,
                            formSectionId: x.uuid!,
                            fieldIsValid: x.jobStatuses && x.jobStatuses.length > 0 ? true : false,
                            fieldErrorMessage: "",
                        })
                    );
                });
            }
        }
    }, [initializeForms]);

    if (newJobRule === null) return <></>;

    const handleConfirm = () => {
        if (isEditing) {
            jobBrokerService.UpdateRule(newJobRule).then((res) => {
                if (res.success) {
                    enqueueCustomSnackbar(t("alert:success.jobRuleUpdated"), "success");
                    navigate(routePaths.settingsJobRules.path);
                } else {
                    enqueueCustomSnackbar(res.message ?? t("alert:error.updatingJobRule"), "error");
                }
            });
        } else {
            jobBrokerService.CreateRule(newJobRule).then((res) => {
                if (res.success) {
                    enqueueCustomSnackbar(t("alert:success.jobRuleUpdated"), "success");
                    navigate(routePaths.settingsJobRules.path);
                } else {
                    enqueueCustomSnackbar(res.message ?? t("alert:error.updatingJobRule"), "error");
                }
            });
        }
    };

    const handleShowJsonOutput = () => {
        if (!openModal) return;

        openModal<ModalShowJsonOutputProps>(ModalShowJsonOutput, { closeModal: () => { }, object: newJobRule });
    };

    if (!newJobRule) return <></>;

    return (
        <>
            <FullTask
                sxStack={{
                    width: "100vw",
                    display: "flex",
                    flexDirection: "column",
                    alignContent: "center",
                    alignItems: "center",
                    ...(theme.helpers(theme).scrollBar as SxProps),
                }}
                TopBar={
                    <FullTaskTopbar
                        sx={{ position: "fixed", top: 0, width: "100%", background: theme.palette.background.default }}
                        title={
                            isEditing
                                ? t("page:fullTaskTopbar.title.jobRuleUpdate", { name: newJobRule.name })
                                : t("page:fullTaskTopbar.title.jobRuleCreation")
                        }
                        backRoute={`${routePaths.settingsJobRules.path}`}
                        rightContent={
                            <>
                                <Button onClick={handleShowJsonOutput} variant="outlined" color="info">
                                    {t("button:showJsonOutput")}
                                </Button>
                                <Button variant="contained" color="primary" onClick={handleConfirm} disabled={!isValid || isLoading}>
                                    {t("common:confirm")}
                                </Button>
                            </>
                        }
                    />
                }
            >
                {isLoading ? (
                    <Stack
                        width={"100%"}
                        height={"500px"}
                        display={"flex"}
                        justifyContent={"center"}
                        alignContent={"center"}
                        alignItems={"center"}
                    >
                        <CircularProgress size={50} />
                    </Stack>
                ) : (
                    <Stack
                        sx={{
                            ...theme.helpers(theme).scrollableContainer,
                            ...theme.helpers(theme).scrollBar,
                        }}
                        width={"100%"}
                        mt={10}
                        pl={2}
                        pr={2}
                        spacing={3}
                    >
                        <Grid container spacing={3}>
                            <Grid size={{ sm: 12, md: 6 }}>
                                <FormSection
                                    size="small"
                                    icon={<InfoIcon />}
                                    defaultExpanded={true}
                                    title={t("form:section.title.basicInfos")}
                                >
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormInputText
                                                label={t("common:name")}
                                                fullWidth={true}
                                                value={newJobRule!.name}
                                                placeholder={t("field:placeholder.jobRuleName")}
                                                onChange={(v) =>
                                                    setNewJobRule(
                                                        produce((draft) => {
                                                            draft!.name = v.target.value;
                                                        })
                                                    )
                                                }
                                                validationProps={{
                                                    formId: formId,
                                                    formSectionId: "sectionJobRuleBasicInfo",
                                                    fieldId: "job-rule-name",
                                                    required: true,
                                                    initialState: false,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormInputText
                                                label={t("common:description")}
                                                fullWidth={true}
                                                value={newJobRule!.description}
                                                placeholder={t("field:placeholder.jobRuleDescription")}
                                                onChange={(v) =>
                                                    setNewJobRule(
                                                        produce((draft) => {
                                                            draft!.description = v.target.value;
                                                        })
                                                    )
                                                }
                                                validationProps={{
                                                    formId: formId,
                                                    formSectionId: "sectionJobRuleBasicInfo",
                                                    fieldId: "job-rule-description",
                                                    required: false,
                                                    initialState: false,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormAutocomplete
                                                fullWidth
                                                options={Object.values(FileCategory)}
                                                label={t("field:label.specifiedInputFileCategoryFilter")}
                                                onChange={(v) => {
                                                    setNewJobRule(
                                                        produce((draft) => {
                                                            if (!draft) return;
                                                            draft.inputFileCategory = v;
                                                        })
                                                    );
                                                }}
                                                value={newJobRule.inputFileCategory}
                                                validationProps={{
                                                    formId: formId,
                                                    formSectionId: "sectionJobRuleBasicInfo",
                                                    fieldId: "inputFileCatergoryFilter",
                                                    required: false,
                                                    initialState: false,
                                                }}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormAutocomplete
                                                label={t("field:label.specifiedTag")}
                                                fullWidth
                                                value={newJobRule.tag}
                                                onChange={(v) => {
                                                    setNewJobRule(
                                                        produce((draft) => {
                                                            if (!draft) return;
                                                            draft.tag = v;
                                                        })
                                                    );
                                                }}
                                                options={Object.values(JobRuleTag)}
                                                validationProps={{
                                                    formId: formId,
                                                    formSectionId: "sectionJobRuleBasicInfo",
                                                    fieldId: "inputSpecifiedTag",
                                                    required: false,
                                                    initialState: false,
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={newJobRule.enabled || false}
                                                        onChange={(e) => {
                                                            setNewJobRule(
                                                                produce((draft) => {
                                                                    if (!draft) return;
                                                                    draft.enabled = e.target.checked;
                                                                })
                                                            );
                                                        }}
                                                    />
                                                }
                                                label={t("field:label.isEnabledSpaceQuestionMark")}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={newJobRule.executeAllDefinitions || false}
                                                        onChange={(e) => {
                                                            setNewJobRule(
                                                                produce((draft) => {
                                                                    if (!draft) return;
                                                                    draft.executeAllDefinitions = e.target.checked;
                                                                })
                                                            );
                                                        }}
                                                    />
                                                }
                                                label={t("field:label.executeAllDefinitions")}
                                            />
                                        </Grid>
                                    </Grid>
                                </FormSection>
                            </Grid>
                            <Grid size={{ sm: 12, md: 6 }}>
                                <FormSection
                                    size="small"
                                    icon={<PermIdentityIcon />}
                                    defaultExpanded={true}
                                    title={t("form:section.title.accessPermissions")}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel shrink>{t("field:label.userGroupAccess")}</InputLabel>
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            options={groups}
                                            getOptionLabel={(option) => option.group?.name || "-"}
                                            value={[...(groups.filter((x) => newJobRule!.groupIds?.includes(x.id)) || [])]}
                                            onChange={(e, value) =>
                                                setNewJobRule(
                                                    produce((draft) => {
                                                        groups ? (draft!.groupIds = value.map((x) => x.id)) : (draft!.groupIds = []);
                                                    })
                                                )
                                            }
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </FormControl>
                                </FormSection>
                            </Grid>
                        </Grid>
                        <FormSection
                            size="small"
                            icon={<FilterListIcon />}
                            defaultExpanded={true}
                            title={t("form:section.title.applicationFilters")}
                        >
                            <Grid container spacing={2} sx={{ mb: 1 }}>
                                <Grid size={{ sm: 12, md: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink>{t("common:objectTypesUpperCase")}</InputLabel>
                                        <Autocomplete
                                            value={[...(newJobRule!.filter?.objectTypes || [])]}
                                            multiple
                                            fullWidth
                                            onChange={(e, v) =>
                                                setNewJobRule(
                                                    produce((draft) => {
                                                        draft!.filter!.objectTypes = v;
                                                    })
                                                )
                                            }
                                            options={Object.values(ObjectType)}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ sm: 12, md: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink>{t("field:label.objectLayer")}</InputLabel>
                                        <Autocomplete
                                            value={[...(newJobRule!.filter?.objectLayers || [])]}
                                            multiple
                                            fullWidth
                                            onChange={(e, v) =>
                                                setNewJobRule(
                                                    produce((draft) => {
                                                        draft!.filter!.objectLayers = v;
                                                    })
                                                )
                                            }
                                            options={Object.values(ObjectLayer)}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ sm: 12, md: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink>{t("field:label.objectClasses")}</InputLabel>
                                        <Autocomplete
                                            value={[...(newJobRule!.filter?.objectClasses || [])]}
                                            multiple
                                            fullWidth
                                            onChange={(e, v) =>
                                                setNewJobRule(
                                                    produce((draft) => {
                                                        draft!.filter!.objectClasses = v;
                                                    })
                                                )
                                            }
                                            options={Object.values(ObjectClass)}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ sm: 12, md: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink>{t("common:objectStatuses")}</InputLabel>
                                        <Autocomplete
                                            value={[...(newJobRule!.filter?.objectStatuses || [])]}
                                            multiple
                                            fullWidth
                                            onChange={(e, v) =>
                                                setNewJobRule(
                                                    produce((draft) => {
                                                        draft!.filter!.objectStatuses = v;
                                                    })
                                                )
                                            }
                                            options={Object.values(ObjectStatus)}
                                            renderInput={(params) => <TextField {...params} />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid size={{ sm: 12, md: 6 }}>
                                    <FormAutocomplete
                                        label={t("field:label.checkForStorageBeforeApply")}
                                        value={newJobRule!.filter?.checkForStorageOnlineCopy}
                                        fullWidth
                                        options={storages.map((x) => x.id) as number[]}
                                        getOptionLabel={(option: number | string) =>
                                            storages.find((x) => x.id === option)?.name || "UNKNOWN"
                                        }
                                        isOptionEqualToValue={(option, value) => option === value}
                                        filterOptions={(x, s) => {
                                            let match = storages.filter((x) => x.name!.toLowerCase().includes(s.inputValue.toLowerCase()));
                                            return x.filter((a) => match.map((s) => s.id).includes(a));
                                        }}
                                        onChange={(v) =>
                                            setNewJobRule(
                                                produce((draft) => {
                                                    draft!.filter!.checkForStorageOnlineCopy = v as number;
                                                })
                                            )
                                        }
                                        validationProps={{
                                            formId: formId,
                                            formSectionId: newJobRule.name!,
                                            fieldId: `jobDefstorages-${newJobRule.name!}`,
                                            required: false,
                                            minLength: 1,
                                            initialValue: newJobRule!.filter?.checkForStorageOnlineCopy || -1,
                                        }}
                                    />
                                </Grid>
                                <MetadataComparator newJobRule={newJobRule} setNewJobRule={setNewJobRule} />
                            </Grid>
                        </FormSection>
                        <EditPostActions formId={formId} newJobRule={newJobRule} setNewJobRule={setNewJobRule} />
                        <EditJobDefs
                            formId={formId}
                            formats={formats}
                            newJobRule={newJobRule}
                            setNewJobRule={setNewJobRule}
                            storages={storages}
                        />
                    </Stack>
                )}
            </FullTask>
        </>
    );
}

export default EditOrCreateJobRule;
